import isEqual from 'lodash/isEqual';
import { monthlyPrice, yearlyPrice } from './price-for-period';
import { findSuitablePrices } from './member-limits';
import checkLimits from './check-limits';
import { getCouponDiscount } from './coupon-utils';
import { DEFAULT_BILLING_PERIOD, DEFAULT_PRODUCT_GROUP } from './constants';

/**
 * @typedef {import('../types').ProductWithPricesAndFeatures} ProductWithPricesAndFeatures
 * @typedef {import('../types').SiteLimits} SiteLimits
 * @typedef {import('../types').PriceWithFeatures} PriceWithFeatures
 */

/**
 *
 * @param {object} price
 * @param {object[]} price.limits
 * @param {string} price.limits.name
 * @param {number} price.limits.value
 *
 * @returns {number} membersIncluded
 */
export function getMembersIncluded(price) {
    const membersIncluded = price.limits.find((limit) => {
        if (limit.name === 'members') {
            return limit.value;
        }
        return null;
    });

    return membersIncluded?.value ?? 0;
}

/**
 *
 * @param {object[]} availablePrices
 * @param {object[]} availablePrices.limits
 * @param {string} availablePrices.billing_period
 * @param {object} selectedPrice
 * @param {string} selectedPrice.billing_period
 * @returns {object} comparablePrice
 */
export function findComparablePrice(availablePrices, selectedPrice) {
    const comparablePrice = availablePrices.find((price) => {
        const sameLimits = isEqual(selectedPrice?.limits, price?.limits);
        const oppositePeriod = price?.billing_period !== selectedPrice?.billing_period;

        return sameLimits && oppositePeriod;
    });

    return comparablePrice;
}

/**
 * @description Filters the array of available products to only include active products.
 * @param {ProductWithPricesAndFeatures[]} availableProducts
 * @returns {ProductWithPricesAndFeatures[]}
 */
export function getActiveAvailableProducts(availableProducts) {
    if (!Array.isArray(availableProducts) || availableProducts.length === 0) {
        return [];
    }

    return availableProducts.filter((product) => !!product.active);
}

/**
 * @description Creates a product ID from a Stripe product ID by removing the `prod_` prefix.
 * @param {string} stripeId
 * @returns {string} the Stripe product ID without the `prod_` prefix
 */
export function createProductIdFromStripeId(stripeId) {
    if (!stripeId) {
        throw new Error('Unable to create product ID from empty Stripe ID');
    }

    if (typeof stripeId !== 'string') {
        throw new Error('Unable to create product ID from non-string Stripe ID');
    }

    if (!stripeId.startsWith('prod_')) {
        throw new Error(`Unable to create product ID from invalid Stripe ID: ${stripeId}`);
    }

    // we remove the `prod_` prefix to use the URL for routing to products in checkout
    return stripeId.replace(/^prod_/, '');
}

/**
 * @description Parses a product ID to a Stripe product ID by adding the `prod_` prefix back.
 * @param {string} productId
 * @returns {string} the Stripe product ID with the `prod_` prefix
 */
export function parseProductIdToStripeId(productId) {
    if (!productId) {
        throw new Error('Unable to parse Stripe ID from empty product ID');
    }

    if (typeof productId !== 'string') {
        throw new Error('Unable to parse Stripe ID from non-string product ID');
    }

    if (productId.startsWith('prod_')) {
        return productId;
    }

    return `prod_${productId}`;
}

/**
 *
 * @param {object} comparePrice
 * @param {number} comparePrice.cost
 * @param {string} comparePrice.billing_period
 * @param {object} suitablePrice
 * @param {number} suitablePrice.cost
 * @param {string} suitablePrice.billing_period
 * @returns {number}
 */
export function getMonthlyEquivalentPrice(comparePrice, suitablePrice) {
    return Math.max(
        monthlyPrice(suitablePrice.cost, suitablePrice.billing_period),
        monthlyPrice(comparePrice.cost, comparePrice.billing_period),
    );
}

/**
 *
 * @param {object} comparePrice
 * @param {number} comparePrice.cost
 * @param {string} comparePrice.billing_period
 * @param {object} suitablePrice
 * @param {number} suitablePrice.cost
 * @param {string} suitablePrice.billing_period
 * @param {object} options
 * @param {boolean} options.percentage
 * @returns {number|null}
 */
export function getAnnualDiscount(comparePrice, suitablePrice, options) {
    const annualBasePrice = getMonthlyEquivalentPrice(comparePrice, suitablePrice) * 12;
    const currentAnnualPrice = yearlyPrice(suitablePrice.cost, suitablePrice.billing_period);
    let diff = 0;

    if (options?.percentage) {
        diff = Math.round(((annualBasePrice - currentAnnualPrice) * 100) / annualBasePrice);
    } else {
        diff = annualBasePrice - currentAnnualPrice;
    }

    if (diff > 0) {
        return diff;
    } else {
        return null;
    }
}

/**
 * @typedef {object} TotalPriceData
 * @param {number} cost
 * @param {number} quantity
 * @param {'year'|'month'} period
 * @param {object[]} selectedAddons
 * @param {object[]} selectedAddons.prices
 * @param {number} selectedAddons.prices.cost
 * @param {string} selectedAddons.prices.billing_period
 * @param {import('stripe').Stripe.Coupon} coupon
 */
/**
 * @param {TotalPriceData} data
 * @returns {number}
 */
export function getTotalPrice({ cost, period, quantity = 1, selectedAddons = [], coupon = {} }) {
    let selectedAddonsCost;

    if (selectedAddons?.length) {
        const selectedAddonsPrices = selectedAddons.map((addonsSelected) => {
            return addonsSelected.prices.filter((price) => {
                return price.billing_period === period;
            })[0];
        });

        // add up all addon prices
        if (selectedAddonsPrices?.length) {
            selectedAddonsCost = selectedAddonsPrices.reduce((total, price) => {
                return total + price.cost;
            }, 0);
        }
    }

    const addons = selectedAddonsCost || 0;
    const basePlan = cost * quantity;
    const currentTotal = addons + basePlan;
    const couponDiscount = getCouponDiscount({ coupon, currentTotal }) || 0;

    return currentTotal - couponDiscount;
}

/**
 *
 * @param {ProductWithPricesAndFeatures[]} availableProducts
 * @param {SiteLimits} currentSiteLimits
 * @param {string} [period]
 * @param {string} [productGroup]
 * @returns {{product: ProductWithPricesAndFeatures, price: PriceWithFeatures}|null}
 */
export function findSuitableProduct(
    availableProducts,
    currentSiteLimits,
    period = DEFAULT_BILLING_PERIOD,
    productGroup = DEFAULT_PRODUCT_GROUP,
) {
    let result = null;
    const currentMembers = currentSiteLimits?.members?.total;
    const toValidate = [];

    if (!availableProducts?.length || !currentSiteLimits) {
        return result;
    }

    if (productGroup) {
        availableProducts = getPurchasableProducts(availableProducts, productGroup);
    }

    availableProducts.forEach((product) => {
        const prices = product.prices;
        const suitableMembersPrices = findSuitablePrices(prices, currentMembers, period);
        if (suitableMembersPrices.length) {
            toValidate.push({
                product: product,
                suitableMembersPrices,
            });
        }
    });

    if (toValidate.length) {
        let foundValidProduct = false;

        // find the first product that has valid prices and return this from this function
        while (!foundValidProduct && toValidate.length) {
            const { product: productData, suitableMembersPrices } = toValidate.shift();

            for (const price of suitableMembersPrices) {
                const limitsValidations = checkLimits(price, {
                    productLimits: productData.limits,
                    currentSiteLimits,
                    product: productData.name,
                });

                if (!limitsValidations.length) {
                    foundValidProduct = true;
                    result = {
                        product: productData,
                        price: price,
                    };
                    break;
                }
            }
        }
    }

    return result;
}

/**
 * @description Removes all products and sorts them by their order value that are not
 * in the purchasable group incl. the trial product.
 * @param {ProductWithPricesAndFeatures[]} availableProducts
 * @param {string} productGroup
 * @returns {ProductWithPricesAndFeatures[]}
 */
export function getPurchasableProducts(availableProducts, productGroup) {
    if (!availableProducts?.length) {
        return [];
    }

    return (
        availableProducts
            .filter(
                (product) =>
                    product?.name?.toLowerCase() !== 'personal' &&
                    product?.name?.toLowerCase() !== 'trial',
            )
            .filter((product) => {
                if (!product?.group || !productGroup) {
                    return true;
                }
                return product.group === productGroup;
            })
            .sort((a, b) => a.order - b.order) ?? []
    );
}
